import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
type GraphRef = { graph: HTMLDivElement | null; text: HTMLDivElement | null };

export default function HomeStackGraph() {
  const graphRefs = useRef<GraphRef[]>([]);
  const triggerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerRef.current,
        start: "top 90%",
        toggleActions: "play none none reverse",
      },
    });

    graphRefs.current.forEach((obj) => {
      if (!obj.graph || !obj.text) return;
      tl.from(
        obj.graph,
        {
          width: "0%",
          duration: 0.7,
          ease: "power2.out",
        },
        "<"
      );
      tl.from(
        obj.text,
        {
          opacity: 0,
          x: -10,
          duration: 0.5,
          ease: "power2.out",
        },
        "<0.5"
      );
    });
  }, []);

  const graphData = [
    {
      name: "Front-end",
      label: "전문 분야",
      percent: 70,
      description: "전문 영역으로, 최신 React 생태계를 활용한 사용자 경험 구현",
      color: "bg-zinc-200",
      textColor: "text-zinc-200",
    },
    {
      name: "Back-end",
      label: "협업 이해",
      percent: 15,
      description: "협업 시야 확장을 위한 API 설계 및 서버 로직 이해",
      color: "bg-zinc-500",
      textColor: "text-zinc-400",
    },
    {
      name: "Infra & Design",
      label: "운영 경험",
      percent: 15,
      description: "배포부터 디자인까지, 서비스의 전체 생명주기 이해",
      color: "bg-zinc-600",
      textColor: "text-zinc-400",
    },
  ];

  return (
    <div className="flex flex-col gap-6 text-sm w-full">
      {/* 통합 프로그레스 바 */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <span>기술 스택 활용 비중</span>
          <span>100%</span>
        </div>

        {/* 단일 프로그레스 바 */}
        <div
          className="relative w-full h-13 bg-zinc-800/50 rounded-lg overflow-hidden flex"
          ref={triggerRef}
        >
          {graphData.map((e, idx) => {
            // 배열 할당
            graphRefs.current[idx] = { graph: null, text: null };
            return (
              <div
                key={idx}
                className={cn(
                  "h-full transition-all duration-700 relative group cursor-pointer",
                  "hover:opacity-90"
                )}
                style={{ width: `${e.percent}%` }}
              >
                {/* progress bar */}
                <div
                  ref={(el) => (graphRefs.current[idx].graph = el!)}
                  className={cn("w-full h-full", e.color)}
                ></div>

                {/* 텍스트 표시 */}
                <div
                  ref={(el) => (graphRefs.current[idx].text = el!)}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 "
                >
                  {e.percent >= 15 ? (
                    // 넓은 영역: 이름 + 퍼센트 세로 배치
                    <>
                      <span
                        className={cn(
                          "text-sm leading-none md:block hidden",
                          e.textColor,
                          idx === 0 ? "text-zinc-800" : "text-zinc-50"
                        )}
                      >
                        {e.name}
                      </span>
                      <span
                        className={cn(
                          "text-[10px] font-bold leading-none",
                          e.textColor,
                          idx === 0 && "text-zinc-800"
                        )}
                      >
                        {e.percent}%
                      </span>
                    </>
                  ) : (
                    // 좁은 영역: 퍼센트만
                    <span className={cn("text-[10px] font-bold", e.textColor)}>
                      {e.percent}%
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* 범례 */}
        <div className="flex items-center gap-4 text-xs">
          {graphData.map((e, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className={cn("w-3 h-3 rounded-sm", e.color)} />
              <span className={e.textColor}>
                {e.name}
                <span className="text-zinc-500 ml-1">({e.label})</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
